import { getApiBaseUrl } from './api';
import { fetchWithAuth } from './api';
import { okJson } from './httpError';

const base = () => getApiBaseUrl();

// The public endpoints below are hit by residents on their own phones, often on weak
// connections, and by link-preview scrapers — a 502 from the edge returns an HTML page, so
// res.json() throws a SyntaxError rather than yielding the `error` field the forms read.
// readPublicJson keeps every public call resolving to a shape the form can render.
async function readPublicJson<T extends { error?: string }>(
  res: Response,
  fallbackError: string,
): Promise<T> {
  const text = await res.text().catch(() => '');
  let data: any = null;
  if (text.trim()) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (data === null || typeof data !== 'object') {
    return { error: fallbackError } as T;
  }
  // Structured AppError responses (validation errors, invalid CPF, etc.) carry the message
  // under `message`/`details`, not `error` — normalize so callers can always show it.
  if (!res.ok && !data.error) {
    data.error = data.details || data.message || fallbackError;
  }
  return data as T;
}

// ─── Public ────────────────────────────────────────────────────────────────

export interface SignupBlock {
  id: string;
  name: string;
  units: { id: string; number: string }[];
}

export interface SignupDepartment {
  id: string;
  name: string;
}

export interface ValidateSignupResponse {
  valid: boolean;
  customer_id?: string;
  customer_name?: string;
  customer_type?: 'condominio' | 'empresa';
  blocks?: SignupBlock[];
  departments?: SignupDepartment[];
  mobile_app_enabled?: boolean;
  cpf_required?: boolean;
  identity_photo_required?: boolean;
  doc_photo_required?: boolean;
  phone_required?: boolean;
  error?: string;
}

export async function validateSignupToken(token: string): Promise<ValidateSignupResponse> {
  let r: Response;
  try {
    r = await fetch(`${base()}/api/v1/public/resident-register/validate/${token}`);
  } catch {
    // Offline / DNS / TLS failure — the registration page shows `error` as its invalid-link
    // state, so resolve rather than reject.
    return { valid: false, error: 'Erro de conexão. Verifique sua internet e tente novamente.' };
  }
  const data = await readPublicJson<ValidateSignupResponse>(r, 'Não foi possível validar o link de cadastro.');
  // A non-OK response must never read as a valid link, whatever the body said.
  if (!r.ok && data.valid) data.valid = false;
  return data;
}

export interface SubmitSelfRegPayload {
  token: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  cpf?: string;
  unit_id?: string;
  department_id?: string;
  relation_type?: string;
  parentesco?: string; // optional free-form kinship note, shown for any relation type
  face_photo_base64?: string;
  /**
   * The full-resolution photo behind face_photo_base64. Optional: the backend stores it
   * best-effort, so older clients that never send it keep working unchanged.
   */
  face_photo_original_base64?: string;
  doc_photo_base64?: string;
  password?: string;
  // Only required when the signup link has mobile_app_enabled (a password is being set in this
  // same submission, granting immediate app login) — obtained via requestRegistrationCode +
  // verifyRegistrationCode below. Plain (admin-reviews-later) links don't need it.
  verification_token?: string;
}

export interface CheckRegistrationEmailResponse {
  /**
   * True when this e-mail ALREADY belongs to a Panther account. Not a refusal: the submission
   * then enrols the person at this unit using the account they already have, which is the whole
   * point of asking. `password_required` is the same answer phrased as an instruction — when it
   * is false the form must drop its "Acesso" step, because no password is being created.
   */
  exists?: boolean;
  password_required?: boolean;
  error?: string;
}

// Checked while the resident is still on the first step, so the form can drop the password step
// before they fill it in rather than discovering the situation at submit.
export async function checkRegistrationEmail(
  token: string,
  email: string,
): Promise<CheckRegistrationEmailResponse> {
  let r: Response;
  try {
    r = await fetch(
      `${base()}/api/v1/public/resident-register/check-email?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`,
    );
  } catch {
    // Offline: resolve as "unknown" rather than reject. The form stays on its default path
    // (ask for a password) and the backend decides for real at submit, so a failed check here
    // never blocks a registration.
    return { error: 'offline' };
  }
  return readPublicJson<CheckRegistrationEmailResponse>(r, 'Não foi possível verificar o e-mail.');
}

// Sends a 6-digit email verification code — required before submitting a self-registration on
// a mobile_app_enabled signup link. Always resolves (backend returns a generic message
// regardless of whether the email/token combo is valid, to avoid leaking which emails exist).
export async function requestRegistrationCode(token: string, email: string): Promise<{ message?: string; error?: string }> {
  const r = await fetch(`${base()}/api/v1/public/resident-register/send-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, email }),
  });
  return readPublicJson(r, 'Erro ao enviar código');
}

// Verifies the code and, on success, returns a short-lived verification_token to include in the
// submitSelfRegistration payload as proof of email ownership.
export async function verifyRegistrationCode(token: string, email: string, code: string): Promise<{ verification_token?: string; error?: string }> {
  const r = await fetch(`${base()}/api/v1/public/resident-register/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, email, code }),
  });
  const data = await readPublicJson<{ verification_token?: string; error?: string }>(r, 'Código incorreto');
  // Never let a non-OK response hand back a token the caller would then treat as verified.
  if (!r.ok) delete data.verification_token;
  return data;
}

export async function submitSelfRegistration(
  payload: SubmitSelfRegPayload,
): Promise<{ success: boolean; id?: string; existing_account?: boolean; error?: string }> {
  const r = await fetch(`${base()}/api/v1/public/resident-register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await readPublicJson<{ success: boolean; id?: string; existing_account?: boolean; error?: string }>(r, 'Erro ao enviar');
  // A non-OK response must never report success, whatever the body said.
  if (!r.ok) data.success = false;
  return data;
}

// ─── Admin ─────────────────────────────────────────────────────────────────

export interface SelfRegistration {
  id: string;
  customer_id: string;
  unit_id?: string;
  department_id?: string;
  relation_type?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  cpf?: string;
  parentesco?: string;
  face_photo_url?: string;
  doc_photo_url?: string;
  status: 'pending' | 'approved' | 'rejected';
  unit?: { id: string; number: string; block: { id: string; name: string } };
  created_at: string;
  updated_at: string;
  rejection_reason?: string;
  // Decision audit — present once a registration leaves "pending". approved_by/rejected_by are
  // raw Keycloak user IDs; reviewed_by_name/email are resolved server-side (people table first,
  // then Keycloak) so the UI never has to render a bare UUID.
  approved_at?: string;
  approved_by?: string;
  rejected_at?: string;
  rejected_by?: string;
  reviewed_by_name?: string;
  reviewed_by_email?: string;
}

export async function getSignupLink(customerId: string): Promise<{ token: string; signup_url: string }> {
  const r = await fetchWithAuth(`${base()}/api/v1/customers/${customerId}/signup-link`);
  return okJson(r, 'Erro ao obter o link de cadastro');
}

// status accepts a single value, a comma-separated list ('approved,rejected') or 'all' — the
// list form backs the decision-history panel without needing one request per status.
export async function listSelfRegistrations(
  customerId: string,
  status = 'pending',
  limit?: number
): Promise<{ items: SelfRegistration[]; total: number }> {
  const qs = new URLSearchParams({ status });
  if (limit) qs.set('limit', String(limit));
  const r = await fetchWithAuth(`${base()}/api/v1/customers/${customerId}/self-registrations?${qs.toString()}`);
  return okJson(r, 'Erro ao carregar os auto-cadastros');
}

// Optional corrections to apply at approval time — lets an admin fix a typo the resident
// submitted (wrong CPF, misspelled name, etc.) instead of rejecting and asking them to
// resubmit. Any field omitted keeps the value the resident originally submitted.
export interface ApproveSelfRegOverrides {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  cpf?: string;
  unit_id?: string;
  department_id?: string;
  relation_type?: string;
  parentesco?: string;
}

export async function approveSelfRegistration(
  customerId: string,
  regId: string,
  personId?: string,
  overrides?: ApproveSelfRegOverrides
): Promise<{ success: boolean; person_id?: string; message?: string; email_status?: 'sent' | 'not_needed' | 'failed' }> {
  const r = await fetchWithAuth(`${base()}/api/v1/customers/${customerId}/self-registrations/${regId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(personId ? { person_id: personId } : {}),
      ...(overrides || {}),
    }),
  });
  // Without this check a 403 resolved to the error body, whose missing `success` field read as
  // a plain failure — losing the reason — and on a 502 the HTML body made r.json() throw a
  // SyntaxError that surfaced verbatim in a toast.
  return okJson(r, 'Erro ao aprovar o auto-cadastro');
}

export async function rejectSelfRegistration(customerId: string, regId: string, reason?: string): Promise<{ success: boolean }> {
  const r = await fetchWithAuth(`${base()}/api/v1/customers/${customerId}/self-registrations/${regId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || '' }),
  });
  // Callers show a success toast and drop the row from the list on a resolved promise, so a
  // missing status check here meant a 403/500 reject reported success and hid a registration
  // that was in fact still pending.
  return okJson(r, 'Erro ao rejeitar o auto-cadastro');
}

export async function resendWelcomeEmail(customerId: string, regId: string): Promise<{ success: boolean; message?: string }> {
  const r = await fetchWithAuth(`${base()}/api/v1/customers/${customerId}/self-registrations/${regId}/resend-welcome`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  return okJson(r, 'Erro ao reenviar e-mail');
}
